<template>
  <div class="p-6">
    <div class="w-full bg-white rounded-xl shadow-md border border-gray-200">
      <div class="px-6 py-4 border-b border-gray-100">
        <h2 class="text-lg font-semibold text-gray-800">Best In Attendance</h2>
        <p class="text-sm text-gray-500">
          This section displays the list of employees recognized for their
          outstanding attendance.
        </p>
      </div>

      <div
        class="px-6 py-4 border-b border-gray-100 flex justify-between items-center"
      >
        <h2 class="text-sm font-semibold text-gray-700">Recognition List</h2>
        <button
          @click="showModal = true"
          class="px-4 py-2 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600"
        >
          + Add Recognition
        </button>
      </div>

      <div class="overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-gray-600 uppercase text-xs">
            <tr>
              <th class="px-4 py-3 text-left">#</th>
              <th class="px-4 py-3 text-left">Employee Name</th>
              <th class="px-4 py-3 text-left">Month of Recognition</th>
              <th class="px-4 py-3 text-left">Date Added</th>
              <th class="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading">
              <td colspan="5" class="text-center text-gray-400 py-8">
                <svg
                  class="animate-spin h-5 w-5 text-orange-400 mx-auto"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
              </td>
            </tr>
            <tr v-else-if="recognitionList.length === 0">
              <td colspan="5" class="text-center text-gray-400 py-8">
                No records found.
              </td>
            </tr>
            <tr
              v-for="(item, index) in paginatedList"
              :key="item.id"
              class="border-t hover:bg-gray-50 transition"
            >
              <td class="px-4 py-3 text-gray-500">
                {{ (currentPage - 1) * perPage + index + 1 }}
              </td>
              <td class="px-4 py-3 text-gray-800 font-medium">
                <div class="flex items-center gap-3">
                  <img
                    :src="item.employee_info?.mediaLink"
                    :alt="item.employee_name"
                    class="w-9 h-9 rounded-full object-cover border border-gray-200"
                    @error="
                      (e) =>
                        (e.target.src =
                          'https://ui-avatars.com/api/?name=' +
                          encodeURIComponent(item.employee_name) +
                          '&background=f97316&color=fff')
                    "
                  />
                  <div>
                    <p class="font-medium text-gray-800">
                      {{ item.employee_name }}
                    </p>
                    <p class="text-xs text-gray-400">
                      {{ item.employee_info?.designation }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 text-gray-600">
                {{ formatMonthYear(item.month_of_recognition) }}
              </td>
              <td class="px-4 py-3 text-gray-600">
                {{ formatLongDateTime(item.createdAt) }}
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click="deleteRecord(item.id)"
                  class="text-red-600 hover:text-red-800 font-medium"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <HeroPagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @page-changed="currentPage = $event"
      />
    </div>

    <BestInAttendanceModal v-model="showModal" @submit="handleSubmit" />
  </div>
</template>

<script>
import { ref, inject, onMounted, computed } from "vue";
import { formatLongDateTime, formatMonthYear } from "@/utils/dateFormat";
import BestInAttendanceModal from "@/components/sections/employees-overview/Modal/BestInAttendanceModal.vue";
import HeroPagination from "@/components/common/HeroPagination.vue";

export default {
  components: { BestInAttendanceModal, HeroPagination },
  setup() {
    const axios = inject("$axios");
    const Swal = inject("$swal");
    const showModal = ref(false);
    const recognitionList = ref([]);
    const isLoading = ref(false);
    const currentPage = ref(1);
    const perPage = 7;

    const totalPages = computed(() =>
      Math.ceil(recognitionList.value.length / perPage)
    );

    const paginatedList = computed(() => {
      const start = (currentPage.value - 1) * perPage;
      return recognitionList.value.slice(start, start + perPage);
    });

    const fetchRecognitions = async () => {
      isLoading.value = true;
      try {
        const { data } = await axios.get("/api/recognitions");
        recognitionList.value = data?.data || data || [];
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: "Failed to load recognition list.",
        });
      } finally {
        isLoading.value = false;
      }
    };

    const handleSubmit = ({ record, employee }) => {
      recognitionList.value.unshift({
        ...record,
        employee_info: {
          mediaLink: employee.mediaLink,
          designation: employee.designation,
          department: employee.department,
        },
      });
      currentPage.value = 1;
    };

    const deleteRecord = async (id) => {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "Are you sure?",
        text: "This record will be permanently deleted.",
        showCancelButton: true,
        confirmButtonColor: "#f97316",
        cancelButtonColor: "#d1d5db",
        confirmButtonText: "Yes, delete it!",
      });

      if (!confirm.isConfirmed) return;

      try {
        await axios.delete(`/api/recognitions/${id}`);
        recognitionList.value = recognitionList.value.filter(
          (item) => item.id !== id
        );
        if (currentPage.value > totalPages.value && totalPages.value > 0) {
          currentPage.value = totalPages.value;
        }
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Record has been deleted.",
          confirmButtonColor: "#f97316",
        });
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: e.response?.data?.message || "Failed to delete record.",
        });
      }
    };

    onMounted(fetchRecognitions);

    return {
      showModal,
      recognitionList,
      isLoading,
      handleSubmit,
      deleteRecord,
      formatLongDateTime,
      formatMonthYear,
      currentPage,
      totalPages,
      paginatedList,
      perPage,
    };
  },
};
</script>
